/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoiceGuidesPage.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Voice Guides listing page with DataGrid for managing
    voice guides from FreeSWITCH.
    
    Refactored to match Extensions module pattern exactly.

 🔁 Last Updated: 2026-01-19 by Rodrigo Cuadra (Match Extensions pattern)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { Volume2, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useVoiceGuidesList } from './hooks';
import { useVoiceGuidesColumns } from './components/VoiceGuidesColumns';
import { VoiceGuidesActions } from './components/VoiceGuidesActions';
import VoiceGuideForm from './components/VoiceGuideForm';
import { VoiceGuide } from './types';

export default function VoiceGuidesPage() {
  const { t } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<VoiceGuide | null>(null);

  // Get column definitions
  const availableColumns = useVoiceGuidesColumns();

  // Use data grid hook with columns
  const {
    data,
    loading,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    columns,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useVoiceGuidesList(availableColumns);

  // Handle edit click - open form with existing data
  const handleEdit = useCallback((item: VoiceGuide) => {
    setEditingItem(item);
    setShowForm(true);
  }, []);

  // Render actions column
  const renderActions = useCallback(
    (item: VoiceGuide) => <VoiceGuidesActions item={item} onEdit={handleEdit} onDeleted={refresh} />,
    [handleEdit, refresh],
  );

  // Handle add button click
  const handleCreate = useCallback(() => {
    setEditingItem(null);
    setShowForm(true);
  }, []);

  // Handle form close
  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setEditingItem(null);
  }, []);

  // Handle form success (generate completed — navigate back)
  const handleFormSuccess = useCallback(() => {
    refresh?.();
    setShowForm(false);
    setEditingItem(null);
  }, [refresh]);

  // Handle refresh only (save settings — stay on form, update editingItem with fresh data)
  const handleFormRefresh = useCallback(async () => {
    // Fetch the item fresh from API so editingItem gets the saved ai_profile_id
    const itemId = editingItem?.uuid ?? editingItem?.id;
    if (itemId) {
      try {
        const { fetchVoiceGuideById } = await import('./api/voiceGuidesApi');
        const updated = await fetchVoiceGuideById(itemId);
        setEditingItem(updated);
        await refresh?.();
      } catch (err) {
        console.error('Failed to refresh voice guide', err);
      }
    }
  }, [editingItem?.uuid, editingItem?.id, refresh]);

  // Sync editingItem when list data refreshes (ONLY if form is open and item was updated)
  // Removed: the previous useEffect([data]) was causing aiProfileId state resets

  // Show form view
  if (showForm) {
    return (
      <div className="p-6">
        <VoiceGuideForm
          editingItem={editingItem}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          onRefresh={handleFormRefresh}
        />
      </div>
    );
  }

  // Show list view (matching Extensions pattern exactly)
  return (
    <div className="p-6">
      <DataGridSimple
        data={data}
        loading={loading}
        columns={columns}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        renderActions={renderActions}
        searchPlaceholder={t('voiceGuides:searchPlaceholder') || 'Search voice guides...'}
        title={t('voiceGuides:title') || 'Voice Guides'}
        titleIcon={<Volume2 className="h-5 w-5 text-primary" />}
        actions={
          <div className="flex items-center gap-2">
            <SimpleTooltip content={t('voiceGuides:actions.add')} position="top">
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('voiceGuides:actions.add') || 'Add'}
              </button>
            </SimpleTooltip>
          </div>
        }
      />
    </div>
  );
}
